import React from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { Button } from 'shop/components'
import CenterModal from 'shop/components/Modal/CenterModal'
import CloseButton from 'shop/components/Modal/CloseButton'
import { overlayOpenAnim } from 'shop/components/Shop/commonStyles'
import { Container, Header, Text } from 'shop/components/shared/ModalComponents'
import {
  CartProductError,
  OrderItemWithVatPrices,
  OrderItem,
  OrderItemWithLimit
} from 'shop/types/cart'
import { getErrorCopy, renderProductItems } from './utils'
import { itemScollListShadows, thinScrollbar } from 'shop/components/common'
import Spinner from 'shop/components/Loader/Spinner'

type Props = {
  invalidItems: (OrderItem | OrderItemWithVatPrices | OrderItemWithLimit)[]
  onContinue: (e: React.MouseEvent) => void
  storeName: string
  productError: CartProductError
  isOpen?: boolean
  onClose?: () => void
  loading?: boolean
  zIndex?: number
  modalProps?: {
    inModal: boolean
    isOverlay: boolean
    isOpen: boolean
    zIndex: number
  }
  backButtonEnabled?: boolean
}

/** Handles single product errors, can handle list of invalid items, displays CTA */
const CartProductErrorModal = ({
  onClose,
  invalidItems,
  onContinue,
  storeName,
  productError,
  isOpen = true,
  loading = false,
  modalProps,
  backButtonEnabled = false
}: Props) => {
  if (!invalidItems.length) return <></>

  const hasMultipleItems = invalidItems.length > 1

  const InvalidProducts = () => (
    <Container>
      <HeaderBody
        errorCopy={getErrorCopy(productError, hasMultipleItems, storeName)}
      />
      <ItemContainer isScrollable={invalidItems.length > 2}>
        {renderProductItems(invalidItems, productError)}
      </ItemContainer>
      <Cta
        onContinue={onContinue}
        onClose={onClose}
        isBackButtonEnabled={backButtonEnabled}
      />
    </Container>
  )

  return (
    <CenterModal
      styles={overlayOpenAnim(isOpen)}
      canClose={false}
      {...modalProps}
    >
      <>
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {backButtonEnabled && (
              <CloseContainer>
                <CloseButton
                  handleCloseModal={onClose}
                  variant='newFulfillmentModal'
                />
              </CloseContainer>
            )}
            <InvalidProducts />
          </>
        )}
      </>
    </CenterModal>
  )
}

const CloseContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    '> div': {
      position: 'absolute',
      top: '24px',
      right: '24px'
    }
  })
)

const SpinnerContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  height: '100%',
  padding: '152px 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontWeight: theme.fontWeights.bold,
  color: 'lightgrey'
}))

export const LoadingState = () => (
  <SpinnerContainer>
    <Spinner size='48px' />
  </SpinnerContainer>
)

export const Cta = React.memo(
  ({
    onContinue,
    onClose,
    isBackButtonEnabled,
    primaryBtnText
  }: {
    onContinue: (e: React.MouseEvent) => void
    onClose?: () => void
    isBackButtonEnabled: boolean
    primaryBtnText?: string
  }) => (
    <>
      <Button
        uppercase={false}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onContinue}
      >
        {primaryBtnText ? primaryBtnText : 'Continue'}
      </Button>
      {isBackButtonEnabled && onClose && (
        <BackContainer onClick={onClose}>
          <Back>Go back</Back>
        </BackContainer>
      )}
    </>
  )
)

export const HeaderBody = ({
  errorCopy
}: {
  errorCopy: { header: string; body: string }
}) => (
  <>
    <Header>{errorCopy.header}</Header>
    <Text>{errorCopy.body}</Text>
  </>
)

export const Back = styled.span<StyledHTMLElement>(
  (): CSSObject => ({
    fontWeight: 500,
    color: '#595959',
    margin: 0
  })
)

export const BackContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    width: 'auto',
    margin: '0 auto',
    padding: '0px 10px',
    cursor: 'pointer'
  })
)

export const ItemContainer = styled.ul<
  StyledHTMLElement & { isScrollable: boolean; marginTop?: string }
>(({ theme, isScrollable, marginTop = '16px' }) => ({
  width: '100%',
  display: 'flex',
  marginBottom: '8px',
  marginTop: marginTop,
  flexDirection: 'column',
  maxHeight: '175px', // ~70px per item - 2.5 items
  ...(isScrollable
    ? {
        overflowY: 'auto',
        marginRight: '-10px',
        paddingRight: '10px',
        ...itemScollListShadows(),
        [theme['mediaQueries']['viewport7']]: {
          marginRight: '-15px',
          paddingRight: '5px'
        },
        ...thinScrollbar(theme)
      }
    : {})
}))

export default CartProductErrorModal
